import { useState, useEffect } from 'react';
import gameTechniques from '../data/techniques.json';

interface Technique {
    id: string;
    name: string;
    description: string;
    tip?: string;
}

interface TechniqueSection {
    level: 'beginner' | 'intermediate' | 'advanced';
    title: string;
    icon: string;
    color: string;
    techniques: Technique[];
}

interface GameTechniques {
    id: string;
    title: string;
    shortName: string;
    imageUrl: string;
    sections: TechniqueSection[];
}

const STORAGE_KEY = 'player-technique-progress';

// Cast imported JSON to typed array
const typedTechniques = gameTechniques as GameTechniques[];

export default function PlayerTechniquesPage() {
    const [selectedGame, setSelectedGame] = useState<string>('smash-bros');
    const [completedTechniques, setCompletedTechniques] = useState<Set<string>>(new Set());
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['beginner', 'intermediate', 'advanced']));

    // Load progress from localStorage
    useEffect(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setCompletedTechniques(new Set(parsed));
            } catch {
                console.error('Failed to parse saved progress');
            }
        }
    }, []);

    // Save progress to localStorage
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...completedTechniques]));
    }, [completedTechniques]);

    const toggleTechnique = (id: string) => {
        setCompletedTechniques(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const toggleSection = (level: string) => {
        setExpandedSections(prev => {
            const next = new Set(prev);
            if (next.has(level)) {
                next.delete(level);
            } else {
                next.add(level);
            }
            return next;
        });
    };

    const currentGame = typedTechniques.find(g => g.id === selectedGame)!;

    const getProgress = (gameId: string) => {
        const game = typedTechniques.find(g => g.id === gameId);
        if (!game) return { completed: 0, total: 0 };
        const total = game.sections.reduce((sum, s) => sum + s.techniques.length, 0);
        const completed = game.sections.reduce(
            (sum, s) => sum + s.techniques.filter(t => completedTechniques.has(t.id)).length,
            0
        );
        return { completed, total };
    };

    const getSectionProgress = (section: TechniqueSection) => {
        const completed = section.techniques.filter(t => completedTechniques.has(t.id)).length;
        return { completed, total: section.techniques.length };
    };

    return (
        <div className="p-6 lg:p-8 max-w-6xl">
            {/* Header */}
            <div className="mb-8 animate-fade-in">
                <h1 className="text-3xl font-display font-bold text-white mb-2">
                    📚 Technique Checklists
                </h1>
                <p className="text-secondary-400">
                    Master competitive techniques from beginner to advanced. Track your progress as you learn!
                </p>
            </div>

            {/* Game Selector */}
            <div className="mb-8">
                <h2 className="text-sm font-semibold text-secondary-400 uppercase tracking-wider mb-4">
                    Select a Game
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {typedTechniques.map(game => {
                        const progress = getProgress(game.id);
                        const isSelected = selectedGame === game.id;
                        return (
                            <button
                                key={game.id}
                                onClick={() => setSelectedGame(game.id)}
                                className={`p-4 rounded-xl border-2 transition-all text-left group ${isSelected
                                    ? 'border-primary-500 bg-primary-500/10'
                                    : 'border-secondary-700 bg-secondary-800/50 hover:border-secondary-600'
                                    }`}
                            >
                                <div className="h-12 w-12 mb-3 rounded-lg overflow-hidden bg-secondary-700">
                                    <img
                                        src={game.imageUrl}
                                        alt={game.title}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <h3 className={`font-semibold text-sm mb-1 ${isSelected ? 'text-primary-400' : 'text-white group-hover:text-primary-400'
                                    }`}>
                                    {game.shortName}
                                </h3>
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 h-1.5 bg-secondary-700 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-primary-500 transition-all"
                                            style={{ width: `${progress.total > 0 ? (progress.completed / progress.total) * 100 : 0}%` }}
                                        />
                                    </div>
                                    <span className="text-xs text-secondary-400">
                                        {progress.completed}/{progress.total}
                                    </span>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Current Game Techniques */}
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-display font-bold text-white">
                        {currentGame.title}
                    </h2>
                    <div className="text-sm text-secondary-400">
                        {getProgress(currentGame.id).completed} / {getProgress(currentGame.id).total} completed
                    </div>
                </div>

                {currentGame.sections.map(section => {
                    const sectionProgress = getSectionProgress(section);
                    const isExpanded = expandedSections.has(section.level);
                    const colorClasses = {
                        green: 'border-green-500/30 bg-green-500/5',
                        yellow: 'border-yellow-500/30 bg-yellow-500/5',
                        red: 'border-red-500/30 bg-red-500/5',
                    };
                    const headerColorClasses = {
                        green: 'text-green-400',
                        yellow: 'text-yellow-400',
                        red: 'text-red-400',
                    };

                    return (
                        <div
                            key={section.level}
                            className={`card border ${colorClasses[section.color as keyof typeof colorClasses]}`}
                        >
                            {/* Section Header */}
                            <button
                                onClick={() => toggleSection(section.level)}
                                className="w-full flex items-center justify-between text-left"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="text-2xl">{section.icon}</span>
                                    <div>
                                        <h3 className={`font-semibold ${headerColorClasses[section.color as keyof typeof headerColorClasses]}`}>
                                            {section.title}
                                        </h3>
                                        <p className="text-xs text-secondary-400">
                                            {sectionProgress.completed} / {sectionProgress.total} completed
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-24 h-2 bg-secondary-700 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full transition-all ${section.color === 'green' ? 'bg-green-500' :
                                                section.color === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'
                                                }`}
                                            style={{ width: `${sectionProgress.total > 0 ? (sectionProgress.completed / sectionProgress.total) * 100 : 0}%` }}
                                        />
                                    </div>
                                    <span className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                                        ▼
                                    </span>
                                </div>
                            </button>

                            {/* Techniques List */}
                            {isExpanded && (
                                <div className="mt-4 space-y-2">
                                    {section.techniques.map(technique => (
                                        <label
                                            key={technique.id}
                                            className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${completedTechniques.has(technique.id)
                                                ? 'bg-secondary-800/50'
                                                : 'bg-secondary-800/20 hover:bg-secondary-800/40'
                                                }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={completedTechniques.has(technique.id)}
                                                onChange={() => toggleTechnique(technique.id)}
                                                className="mt-1 w-5 h-5 rounded border-secondary-600 text-primary-500 focus:ring-primary-500 focus:ring-offset-secondary-800"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <h4 className={`font-medium ${completedTechniques.has(technique.id)
                                                    ? 'text-secondary-400 line-through'
                                                    : 'text-white'
                                                    }`}>
                                                    {technique.name}
                                                </h4>
                                                <p className="text-sm text-secondary-400 mt-0.5">
                                                    {technique.description}
                                                </p>
                                                {technique.tip && (
                                                    <p className="text-xs text-primary-400 mt-1">
                                                        💡 {technique.tip}
                                                    </p>
                                                )}
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Footer */}
            <div className="mt-8 text-center text-secondary-500 text-sm">
                <p>Progress is saved automatically. Keep practicing!</p>
            </div>
        </div>
    );
}
